// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, h, Prop, Watch } from '@stencil/core'

import { attachInternals } from '../../helpers'
import type { Importance } from '../shared'

/**
 * The `<qds-divider>` element a thematic break between paragraph-level
 * elements: for example, a change of scene in a story, or a shift of topic
 * within a section.
 *
 * Dividers can also be used to group menu items in `<qds-dropdown>` elements.
 *
 * @see https://quartz.se.com/build/components/divider
 */
@Component({
  tag: 'qds-divider',
  shadow: true,
  styleUrl: 'divider.css',
})
export class Divider implements ComponentInterface {
  /**
   * The divider's importance.
   */
  @Prop() public readonly importance?: Importance = 'standard'

  /**
   * Display the divider in vertical orientation.
   */
  @Prop() public readonly vertical: boolean = false

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  readonly #internals: ElementInternals = attachInternals.call(this)

  get #computedImportance(): Importance {
    switch (this.importance) {
      case 'emphasized':
      case 'subdued': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  @Watch('vertical')
  protected verticalChanged(): void {
    this.#internals.ariaOrientation = this.vertical ? 'vertical' : 'horizontal'
  }

  public componentWillLoad(): void {
    this.#internals.role = 'separator'
    this.#internals.ariaHidden = 'true'

    this.verticalChanged()
  }

  public render() {
    return (
      <hr
        class={{
          'qds-divider': true,
          'qds-vertical': this.vertical,
        }}
        aria-hidden="true"
        data-importance={this.#computedImportance}
      />
    )
  }
}
